/**
 * webSpaceBossDraw.web.ts
 * Canvas2D draw helpers for the World 2 boss + its weapons (turret
 * bolts and missile barrage) plus a sibling helper for enemy weapons
 * that share the "hostile projectile" visual class (fighter-drone
 * lasers). Extracted from `webSpaceDraw.web.ts` so the parent file
 * stays under the AGENTS.md 300-line cap once the three boss sprite
 * sheets and their test hooks were wired in.
 *
 * Each helper prefers a decoded sprite via `ctx.drawImage` and falls
 * back to the greybox colour `ctx.fillRect` during the brief decode
 * window so a volley is never invisible. The module-local
 * `ImageSlot`s are owned by this file; the parent calls
 * {@link drawSpaceBossLayer} per frame and {@link paintFallbackOrSprite}
 * is intentionally NOT exported — callers go through the layer entry.
 *
 * @module webSpaceBossDraw
 */

/// <reference lib="dom" />

import {
  type ImageSlot,
  createImageSlot,
  ensureImageLoaded,
} from './lazyImageLoader';
import { ensureEntitySprite } from './spaceEntitySpriteCache';
import type { SpaceDrawList } from './spaceRender';
import bossSpriteUrl from '../../assets/sprites/void-mothership.png';
import bossBoltUrl from '../../assets/sprites/boss-bolt.png';
import bossMissileUrl from '../../assets/sprites/boss-missile.png';

// DECISION: Vite static-asset imports (replaced original `require('...png')`
// calls that ADR-011 left behind — browser has no `require`, so the originals
// crashed the bundle at module-load time).
const BOSS_SPRITE_MODULE: string = bossSpriteUrl;
const BOSS_BOLT_MODULE: string = bossBoltUrl;
const BOSS_MISSILE_MODULE: string = bossMissileUrl;

// Module-level cache slots — one per asset. `lazyImageLoader.web.ts`
// owns the actual decode lifecycle (single-shot latch, `expo-asset`
// resolution, silent-fail on missing runtime).
const bossSpriteSlot: ImageSlot = createImageSlot();
const bossBoltSlot: ImageSlot = createImageSlot();
const bossMissileSlot: ImageSlot = createImageSlot();

/**
 * Test-only hook: install a decoded boss spritesheet into the cache so
 * a unit test can exercise the `drawImage` path for the capital ship.
 *
 * @param img - Decoded image to install as the boss sprite, or `null`
 *   to clear the cache and reset the lazy-load latch.
 * @returns void — mutates module-level cache state as a side effect.
 */
export function _setBossSpriteForTests(
  img: globalThis.HTMLImageElement | null,
): void {
  bossSpriteSlot.current = img;
  bossSpriteSlot.loadStarted = img !== null;
}

/**
 * Test-only hook: install a decoded boss-bolt sprite into the cache so
 * a unit test can exercise the `drawImage` path for boss projectiles.
 *
 * @param img - Decoded image to install as the boss-bolt sprite, or
 *   `null` to clear the cache and reset the lazy-load latch.
 * @returns void — mutates module-level cache state as a side effect.
 */
export function _setBossBoltSpriteForTests(
  img: globalThis.HTMLImageElement | null,
): void {
  bossBoltSlot.current = img;
  bossBoltSlot.loadStarted = img !== null;
}

/**
 * Test-only hook: install a decoded boss-missile sprite into the cache
 * so a unit test can exercise the `drawImage` path for boss missiles.
 *
 * @param img - Decoded image to install as the boss-missile sprite, or
 *   `null` to clear the cache and reset the lazy-load latch.
 * @returns void — mutates module-level cache state as a side effect.
 */
export function _setBossMissileSpriteForTests(
  img: globalThis.HTMLImageElement | null,
): void {
  bossMissileSlot.current = img;
  bossMissileSlot.loadStarted = img !== null;
}

/**
 * Paint the boss + its weapons onto `ctx` for the current frame.
 *
 * Z-order inside this fragment, painted in source order:
 *   boss → boss-shield ring → boss turret bolts → boss missiles.
 *
 * Shield-ring nodes paint directly on top of the boss so the orbiting
 * pattern reads against the hull. Builder gates emission on
 * `isShielded && HOLD`, so an empty array here matches the open damage
 * window one-to-one.
 *
 * @param ctx - 2D rendering context for the game canvas.
 * @param drawList - Read-only draw list for the current frame.
 * @returns void — paints onto `ctx` as a side effect.
 */
export function drawSpaceBossLayer(
  ctx: CanvasRenderingContext2D,
  drawList: SpaceDrawList,
): void {
  ensureImageLoaded(bossSpriteSlot, BOSS_SPRITE_MODULE);
  ensureImageLoaded(bossBoltSlot, BOSS_BOLT_MODULE);
  ensureImageLoaded(bossMissileSlot, BOSS_MISSILE_MODULE);

  // Boss paints after regular enemies so its silhouette stays
  // unambiguous when a fighter / bomber drifts across the capital ship.
  if (drawList.boss !== null) {
    const b = drawList.boss;
    if (bossSpriteSlot.current !== null) {
      ctx.drawImage(bossSpriteSlot.current, b.x, b.y, b.width, b.height);
    } else {
      ctx.fillStyle = b.color;
      ctx.fillRect(b.x, b.y, b.width, b.height);
    }
  }

  for (const s of drawList.bossShield) {
    // DECISION: Shield-node sprite shares the spaceEntitySpriteCache
    // path so its decode latch is centralised with the asteroid / mine
    // cache. Colour-rect fallback during the first-frame decode window
    // matches the bolt + missile paths above.
    const shieldImg =
      s.spriteId !== undefined ? ensureEntitySprite(s.spriteId) : null;
    if (shieldImg !== null) {
      ctx.drawImage(shieldImg, s.x, s.y, s.width, s.height);
    } else {
      ctx.fillStyle = s.color;
      ctx.fillRect(s.x, s.y, s.width, s.height);
    }
  }

  // Boss turret bolts paint after the boss so they look like they're
  // streaming out of it rather than passing behind. Sprite path wins
  // when the boss-bolt texture has decoded; colour rect is the fallback.
  for (const bp of drawList.bossProjectiles) {
    if (bossBoltSlot.current !== null) {
      ctx.drawImage(bossBoltSlot.current, bp.x, bp.y, bp.width, bp.height);
    } else {
      ctx.fillStyle = bp.color;
      ctx.fillRect(bp.x, bp.y, bp.width, bp.height);
    }
  }

  // Boss missiles paint with the bolts — same "streaming from the boss"
  // visual ordering, different palette + source array. Sprite path
  // wins once the boss-missile texture has decoded.
  for (const bm of drawList.bossMissiles) {
    if (bossMissileSlot.current !== null) {
      ctx.drawImage(bossMissileSlot.current, bm.x, bm.y, bm.width, bm.height);
    } else {
      ctx.fillStyle = bm.color;
      ctx.fillRect(bm.x, bm.y, bm.width, bm.height);
    }
  }
}

/**
 * Paint the fighter-drone laser layer onto `ctx`.
 *
 * Greybox-only — there is no laser sprite at this slice, so every bolt
 * paints as a colour rect. The function is its own layer (not folded
 * into the boss layer) so the caller can choose z-order: fighter
 * lasers paint between the enemies block and the mines block in the
 * parent file so they sit visually behind heavier hazards.
 *
 * @param ctx - 2D rendering context for the game canvas.
 * @param drawList - Read-only draw list for the current frame.
 * @returns void — paints onto `ctx` as a side effect.
 */
export function drawFighterLasersLayer(
  ctx: CanvasRenderingContext2D,
  drawList: SpaceDrawList,
): void {
  for (const fl of drawList.fighterLasers) {
    ctx.fillStyle = fl.color;
    ctx.fillRect(fl.x, fl.y, fl.width, fl.height);
  }
}

/**
 * Paint the bomber cluster-bomb + shrapnel layers onto `ctx`.
 *
 * Greybox-only — there is no bomb sprite at this slice, so every
 * payload + fragment paints as a colour rect. Bombs paint first so
 * shrapnel from a same-frame detonation reads as sitting on top of
 * the (just-dead) bomb's last visible position. The function is its
 * own layer (sibling to {@link drawFighterLasersLayer}) so the caller
 * can choose z-order: bomber ordnance paints in the same enemy-weapon
 * band as fighter lasers in `webSpaceDraw`.
 *
 * @param ctx - 2D rendering context for the game canvas.
 * @param drawList - Read-only draw list for the current frame.
 * @returns void — paints onto `ctx` as a side effect.
 */
export function drawBomberOrdnanceLayer(
  ctx: CanvasRenderingContext2D,
  drawList: SpaceDrawList,
): void {
  for (const cb of drawList.clusterBombs) {
    ctx.fillStyle = cb.color;
    ctx.fillRect(cb.x, cb.y, cb.width, cb.height);
  }
  for (const bs of drawList.bombShrapnel) {
    ctx.fillStyle = bs.color;
    ctx.fillRect(bs.x, bs.y, bs.width, bs.height);
  }
}
